import React from "react";
import { Heart } from "lucide-react";
import { useAddLeadFavorite, useRemoveLeadFavorite, useLeadFavoriteIDs } from "../../hooks/useLeadsFavorites";
import { makeLeadFavoriteKey } from "../../services/LeadsFavoritesService";
import { getIsActionLoading, getIsDisabled } from "./LeadSaveButton.utils";
import { useTranslation } from "../../context/LanguageContext";

interface LeadSaveButtonProps {
  leadId: string;
  retailer: string;
  className?: string;
  compact?: boolean;
}

const LeadSaveButton: React.FC<LeadSaveButtonProps> = ({ leadId, retailer, className = "", compact = false }) => {
  const { t } = useTranslation();
  const compositeKey = makeLeadFavoriteKey(retailer, leadId);
  const { data: savedIds, isLoading: isCheckingFavorite } = useLeadFavoriteIDs(retailer);
  const isFavorite = (savedIds ?? []).includes(compositeKey);

  const addMutation = useAddLeadFavorite(retailer, false);
  const removeMutation = useRemoveLeadFavorite(retailer, false);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFavorite) {
      removeMutation.mutate(compositeKey);
    } else {
      addMutation.mutate({ leadId, retailer });
    }
  };

  const isActionLoading = getIsActionLoading(addMutation.isPending, removeMutation.isPending);
  const isDisabled = getIsDisabled(isCheckingFavorite, isActionLoading);

  return (
    <button
      onClick={handleToggle}
      disabled={isDisabled}
      className={`group relative inline-flex items-center ${
        compact ? "p-1.5 rounded-full" : "gap-1.5 px-2.5 py-1.5 rounded-lg"
      } text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
        isActionLoading
          ? "bg-rose-100 text-rose-700 cursor-not-allowed"
          : isFavorite
          ? "bg-rose-100 text-rose-700 hover:bg-rose-200 hover:shadow-sm active:scale-95"
          : "bg-rose-50 text-rose-700 hover:bg-rose-100 hover:shadow-sm active:scale-95"
      } ${className}`}
      title={isFavorite ? t("leads.removeFromSaved") : t("leads.addToSaved")}
    >
      {isActionLoading ? (
        <>
          <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {!compact && <span>{isFavorite ? t("leads.removing") : t("leads.adding")}</span>}
        </>
      ) : (
        <>
          <Heart className={`w-3.5 h-3.5 transition-all duration-200 ${isFavorite ? "fill-current scale-110" : "group-hover:scale-110"}`} />
          {!compact && <span>{isFavorite ? t("leads.saved") : t("leads.save")}</span>}
        </>
      )}
      <div className={`absolute inset-0 ${compact ? "rounded-full" : "rounded-lg"} transition-all duration-200 ${
        isFavorite
          ? "bg-gradient-to-r from-rose-600/0 to-rose-700/0 group-hover:from-rose-600/5 group-hover:to-rose-700/5"
          : "bg-gradient-to-r from-rose-500/0 to-rose-600/0 group-hover:from-rose-500/5 group-hover:to-rose-600/5"
      }`} />
    </button>
  );
};

export default LeadSaveButton;
